<template>
  <div class="moniteur-expert">
    <!-- Zone principale avec le tachogramme -->
    <div
      ref="mainArea"
      class="main-area"
      :class="{ 'fullscreen-mode': isFullscreen }"
    >
      <div class="card tachogram-container">
        <!-- Bouton de démarrage au centre du tachogramme -->
        <div v-if="!exerciseStarted" class="start-button-container">
          <!-- Message capteur mal positionné sur l'écran de démarrage -->
          <div v-if="isMalConnected" class="sensor-warning-overlay">
            <div class="sensor-warning-card">
              <div class="warning-icon-large">⚠️</div>
              <h3>Capteur mal positionné</h3>
              <p>
                Vérifiez le placement du capteur avant de démarrer l'exercice
              </p>
              <div class="warning-details">
                <span class="bpm-indicator"
                  >BPM détecté: {{ bpmData.bpm }}</span
                >
              </div>
            </div>
          </div>

          <!-- Contenu normal de démarrage -->
          <div
            class="start-content-center"
            :class="{ blurred: isMalConnected }"
          >
            <div class="start-icon">
              <svg width="80" height="80" viewBox="0 0 24 24" fill="none">
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="#489181"
                  stroke-width="2"
                />
                <polygon points="10,8 16,12 10,16" fill="#489181" />
              </svg>
            </div>
            <h2>Prêt à commencer</h2>
            <p>Démarrez votre session de cohérence cardiaque</p>
            <button
              @click="startExercise"
              class="start-exercise-btn"
              :disabled="isMalConnected"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <polygon points="5,3 19,12 5,21" fill="currentColor" />
              </svg>
              Démarrer l'exercice
            </button>
          </div>
        </div>

        <!-- Tachogramme quand exercice en cours -->
        <div v-else class="tachogram-wrapper">
          <!-- Message capteur mal positionné pendant l'exercice -->
          <div v-if="isMalConnected" class="sensor-warning-during-exercise">
            <div class="warning-banner">
              <span class="warning-icon">⚠️</span>
              <div class="warning-content">
                <strong>Capteur mal positionné</strong>
                <span class="warning-subtitle"
                  >Repositionnez le capteur pour continuer</span
                >
              </div>
              <span class="bpm-value">{{ bpmData.bpm }} BPM</span>
            </div>
          </div>

          <Tachogram
            :bpm="bpmData.bpm"
            :isFullscreen="isFullscreen"
            :time="exerciseTime"
            :exerciseStarted="exerciseStarted"
            @toggle-fullscreen="toggleFullscreen"
          />
        </div>
      </div>

      <!-- Grille de 4 éléments avec bouton overlay -->
      <div class="card metrics-container">
        <!-- Bouton Terminer en overlay -->
        <div v-if="exerciseStarted" class="stop-button-overlay">
          <button
            @click="stopExercise"
            class="control-button stop-btn stop-btn-overlay"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <rect
                x="6"
                y="6"
                width="12"
                height="12"
                rx="2"
                fill="currentColor"
              />
            </svg>
            Terminer
          </button>
        </div>

        <div class="metrics-grid">
          <!-- Première case - BPM Bubble -->
          <div v-if="metricVisibility.bpm && !isMobile" class="metric-card">
            <button
              class="visibility-toggle"
              @click="toggleMetricVisibility('bpm')"
              title="Masquer BPM Instantané"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                <circle cx="12" cy="12" r="3"></circle>
              </svg>
            </button>
            <BpmBubble :bpm="bpmData.bpm" />
            <h3 class="metric-title">BPM Instantané</h3>
          </div>
          <div v-else-if="!isMobile" class="metric-card-placeholder" @click="toggleMetricVisibility('bpm')">
            <div class="placeholder-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>
            </div>
            <span class="placeholder-text">Afficher BPM Instantané</span>
          </div>

          <!-- Deuxième case - Guide Respiratoire -->
          <div v-if="metricVisibility.breathing" class="metric-card">
            <button
              class="visibility-toggle"
              @click="toggleMetricVisibility('breathing')"
              title="Masquer Guide Respiratoire"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                <circle cx="12" cy="12" r="3"></circle>
              </svg>
            </button>
            <BreathingGuide />
            <h3 class="metric-title">Guide Respiratoire</h3>
          </div>
          <div v-else class="metric-card-placeholder" @click="toggleMetricVisibility('breathing')">
            <div class="placeholder-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>
            </div>
            <span class="placeholder-text">Afficher Guide Respiratoire</span>
          </div>

          <!-- Troisième case - Cohérence Cardiaque -->
          <div v-if="metricVisibility.coherence" class="metric-card">
            <button
              class="visibility-toggle"
              @click="toggleMetricVisibility('coherence')"
              title="Masquer Cohérence Cardiaque"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                <circle cx="12" cy="12" r="3"></circle>
              </svg>
            </button>
            <CoherenceCardiaqueGauge :coherenceValue="bpmData.cc" />
            <h3 class="metric-title">Cohérence Cardiaque</h3>
          </div>
          <div v-else class="metric-card-placeholder" @click="toggleMetricVisibility('coherence')">
            <div class="placeholder-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>
            </div>
            <span class="placeholder-text">Afficher Cohérence Cardiaque</span>
          </div>

          <!-- Quatrième case - Distribution des Pics -->
          <div v-if="metricVisibility.distribution" class="metric-card">
            <button
              class="visibility-toggle"
              @click="toggleMetricVisibility('distribution')"
              title="Masquer Distribution des Pics"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                <circle cx="12" cy="12" r="3"></circle>
              </svg>
            </button>
            <DistributionPicsCircle
              :pctT1="bpmData.pctT1"
              :pctT2="bpmData.pctT2"
              :pctT3="bpmData.pctT3"
              :coherenceValue="bpmData.cc"
              :exerciseStarted="exerciseStarted"
            />
            <h3 class="metric-title">Distribution des Pics</h3>
          </div>
          <div v-else class="metric-card-placeholder" @click="toggleMetricVisibility('distribution')">
            <div class="placeholder-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>
            </div>
            <span class="placeholder-text">Afficher Distribution des Pics</span>
          </div>
        </div>
      </div>
    </div>

    <ReplayModal
      v-if="showReplayModal"
      :visible="showReplayModal"
      :session="lastSessionData"
      @close="showReplayModal = false"
      @save-exercise="handleSaveExercise"
    />
  </div>
</template>
<script>
import { connectSymbioCenter } from "@/services/symbiosensor/SocketService";
import BpmBubble from "@/components/monitorage/moniteurExpert/BpmBubble.vue";
import BreathingGuide from "@/components/monitorage/moniteurExpert/GuideRespiratoire.vue";
import Tachogram from "@/components/monitorage/moniteurExpert/Tachogram.vue";
import CoherenceCardiaqueGauge from "@/components/monitorage/moniteurExpert/CoherenceCardiaqueGauge.vue";
import DistributionPicsCircle from "@/components/monitorage/moniteurExpert/DistributionPicsCircle.vue";
import ReplayModal from "@/components/monitorage/moniteurExpert/ReplayModal.vue";
import Parse from "parse";
import Swal from "sweetalert2/dist/sweetalert2.js";

const Toast = Swal.mixin({
  toast: true,
  position: "bottom-end",
  showConfirmButton: false,
  timer: 6000,
  timerProgressBar: true,
  didOpen: (toast) => {
    toast.addEventListener("mouseenter", Swal.stopTimer);
    toast.addEventListener("mouseleave", Swal.resumeTimer);
  },
});

export default {
  name: "MoniteurExpert",
  data() {
    return {
      symbioService: null,
      isConnected: false,
      hasSensorData: false,
      exerciseStarted: false,
      isFullscreen: false,
      exerciseTime: 0,
      timerInterval: null,
      exerciseData: [], // Stockage des données pendant l'exercice
      bpmData: {
        bpm: 0,
        rr: 0,
        cc: 0,
        pctT1: 0,
        pctT2: 0,
        pctT3: 0,
        traceTacho: [],
      },
      showReplayModal: false,
      lastSessionData: null,
      metricVisibility: {
        bpm: true,
        breathing: true,
        coherence: true,
        distribution: true,
      },
      // Variables pour gérer le plein écran personnalisé
      fullscreenElement: null,
      originalStyles: {},
      isMobile: window.innerWidth <= 480, // Ajouté pour la détection mobile réactive
    };
  },
  components: {
    BpmBubble,
    BreathingGuide,
    Tachogram,
    CoherenceCardiaqueGauge,
    DistributionPicsCircle,
    ReplayModal,
  },
  computed: {
    isMalConnected() {
      return (
        this.hasSensorData && this.bpmData.bpm > 0 && this.bpmData.bpm < 40
      );
    },
    getConnectionStatusClass() {
      if (!this.isConnected) return "disconnected";
      if (this.isMalConnected) return "mal-connected";
      if (this.hasSensorData) return "connected";
      return "waiting";
    },
    connectionStatus() {
      if (!this.isConnected) return "Déconnecté";
      if (this.isMalConnected) return "Capteur mal positionné";
      return this.hasSensorData
        ? "Connecté - Données reçues"
        : "Connecté - En attente de données";
    },
  },
  watch: {
    // Surveiller les changements de statut de connexion
    isMalConnected(newVal, oldVal) {
      if (newVal && this.exerciseStarted) {
        // Optionnel : arrêter l'exercice si le capteur devient mal connecté
        console.warn("Capteur mal positionné détecté pendant l'exercice");
      }
    },
  },
  created() {
    this.symbioService = connectSymbioCenter({
      onBpmUpdate: this.handleBpmUpdate,
      onConnectionChange: this.handleConnectionChange,
      os: this.detectOS(),
    });

    this.symbioService.connect();
  },
  mounted() {
    // Écouter les touches pour quitter le plein écran avec Echap
    document.addEventListener("keydown", this.handleKeydown);
    // Écouteur pour la détection mobile réactive
    window.addEventListener("resize", this.handleResizeMobile);
  },
  beforeUnmount() {
    if (this.symbioService) {
      this.symbioService.disconnect();
    }

    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }

    // Nettoyer les event listeners
    document.removeEventListener("keydown", this.handleKeydown);
    window.removeEventListener("resize", this.handleResizeMobile);

    // Quitter le plein écran si actif
    if (this.isFullscreen) {
      this.exitCustomFullscreen();
    }
  },
  methods: {
    toggleMetricVisibility(metric) {
      this.metricVisibility[metric] = !this.metricVisibility[metric];
    },
    detectOS() {
      if (navigator.platform.indexOf("Mac") !== -1) return "Mac";
      return "Windows";
    },

    async handleSaveExercise(sessionData) {
      try {
        // Tenter de sauvegarder les données
        await this.saveExerciseData(sessionData);

        // Afficher un toast de succès
        Toast.fire({
          icon: "success",
          title: "Votre exercice a bien été sauvegardé !",
        });

        // Réinitialiser tous les états du moniteur après la sauvegarde
        this.resetMonitor();

        // Émettre un événement pour signaler que l'exercice est terminé
        setTimeout(() => {
          this.$emit("moniteur-finish", true);
        }, 1000); // L'event est émis après 1 seconde pour permettre à l'utilisateur de voir le toast
      } catch (error) {
        // Si une erreur survient, afficher un toast d'échec
        Toast.fire({
          icon: "error",
          title: "L'exercice n'a pas pu être sauvegardé.",
        });
      }
    },
    resetMonitor() {
      // Réinitialisation des variables liées à l'exercice
      this.exerciseStarted = false; // Exercice non démarré
      this.exerciseTime = 0; // Temps de l'exercice à zéro
      this.exerciseData = []; // Réinitialisation des données collectées
      this.bpmData = {
        // Réinitialisation des données BPM
        bpm: 0,
        rr: 0,
        cc: 0,
        pctT1: 0,
        pctT2: 0,
        pctT3: 0,
        traceTacho: [],
      };

      // Réinitialisation des indicateurs de connexion
      this.isConnected = false;
      this.hasSensorData = false;

      // Réinitialisation des variables liées au capteur
      this.isMalConnected = false; // Si le capteur est mal connecté

      // Réinitialisation du mode plein écran
      if (this.isFullscreen) {
        this.exitCustomFullscreen();
      }

      // Réinitialisation de la modale de relecture
      this.showReplayModal = false;
      this.lastSessionData = null;

      // Réinitialisation de l'interface utilisateur (ex: masques ou surcharges visuelles)
      // Exemple : réinitialisation du message de capteur mal positionné
      // ou de tout autre visuel spécifique
      this.isMalConnected = false;
    },
    handleBpmUpdate(data) {
      this.bpmData = { ...data };

      // Collecter les données pendant l'exercice
      if (this.exerciseStarted) {
        this.exerciseData.push({
          timestamp: Date.now(),
          time: this.exerciseTime,
          ...data,
        });
      }

      console.log("BPM mis à jour:", data);

      // Log spécifique pour les cas de mauvais branchement
      if (this.isMalConnected) {
        console.warn(`Capteur mal positionné détecté - BPM: ${data.bpm}`);
      }
    },

    handleConnectionChange(status) {
      this.isConnected = status.isSocketConnected;
      this.hasSensorData = status.hasSensorData;
      console.log("Statut de connexion:", status);
    },

    startExercise() {
      if (this.isMalConnected) {
        console.warn(
          "Impossible de démarrer l'exercice - capteur mal positionné"
        );
        return;
      }

      this.exerciseStarted = true;
      this.exerciseTime = 0;
      this.exerciseData = []; // Réinitialiser les données
      this.startTimer();
      console.log("Exercice démarré");
    },

    stopExercise() {
      this.exerciseStarted = false;
      this.stopTimer();

      // Préparer les données de relecture
      const sessionData = this.generateSessionReport();

      // Ouvre la modale de relecture
      this.lastSessionData = sessionData;
      this.showReplayModal = true;

      // Tu peux garder aussi l'emit global si besoin
      this.$emit("exercise-completed", sessionData);
      this.sendDataToReplayService(sessionData);

      // Appeler la méthode saveExo sur le serveur
      this.saveExerciseData(sessionData);
    },

    startTimer() {
      this.timerInterval = setInterval(() => {
        this.exerciseTime++;
      }, 1000);
    },

    stopTimer() {
      if (this.timerInterval) {
        clearInterval(this.timerInterval);
        this.timerInterval = null;
      }
    },

    formatTimer(seconds) {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${mins.toString().padStart(2, "0")}:${secs
        .toString()
        .padStart(2, "0")}`;
    },

    /**
     * Méthode optimisée pour basculer le plein écran sur la main-area uniquement
     */
    async toggleFullscreen() {
      try {
        if (!this.isFullscreen) {
          this.enterCustomFullscreen();
        } else {
          this.exitCustomFullscreen();
        }
      } catch (error) {
        console.error("Erreur lors du changement de mode plein écran:", error);
      }
    },
    async saveExerciseData(sessionData) {
      try {
        // Préparer les données à envoyer à la méthode saveExo
        const params = {
          resultat: JSON.stringify({
            ArrayTacho: sessionData.ArrayTacho,
            TabCC: sessionData.TabCC,
            AverageCC: sessionData.AverageCC,
            TimeCompletion: sessionData.TimeCompletion,
            Timer: sessionData.Timer,
            BpmCC: sessionData.BpmCC,
            Pct1: sessionData.Pct1,
            Pct2: sessionData.Pct2,
            Pct3: sessionData.Pct3,
            Cpm: sessionData.Cpm,
            Profil: sessionData.Profil,
            Inspi: sessionData.Inspi,
            PauseInspi: sessionData.PauseInspi,
            Expi: sessionData.Expi,
            PauseExpi: sessionData.PauseExpi,
          }),
          idPatientTherapeute: null, // Vous pouvez mettre l'ID si nécessaire
          idUserForSave: null, // Vous pouvez mettre l'ID si nécessaire
          elapsedTime: sessionData.TimeCompletion,
          note: 0, // Mettez ici la note si nécessaire
          typeExercice: "MoniteurExpert",
          fileReçuId: "3JswDn3wdS", // Vous pouvez définir un ID de fichier si nécessaire
          comment: sessionData.comment || "", // Si vous avez un champ de commentaire
        };

        // Appel à la méthode Cloud 'saveExo' sur Parse
        const response = await Parse.Cloud.run("saveExo", params);
        console.log("Exercice sauvegardé avec succès:", response);
      } catch (error) {
        console.error("Erreur lors de la sauvegarde de l'exercice:", error);
      }
    },

    /**
     * Entre en mode plein écran personnalisé pour la main-area
     */
    enterCustomFullscreen() {
      const mainArea = this.$refs.mainArea;
      if (!mainArea) return;

      // Sauvegarder les styles originaux
      this.originalStyles = {
        position: mainArea.style.position,
        top: mainArea.style.top,
        left: mainArea.style.left,
        width: mainArea.style.width,
        height: mainArea.style.height,
        zIndex: mainArea.style.zIndex,
        backgroundColor: mainArea.style.backgroundColor,
        overflow: mainArea.style.overflow,
      };

      // Appliquer les styles plein écran
      Object.assign(mainArea.style, {
        position: "fixed",
        top: "0",
        left: "0",
        width: "100vw",
        height: "100vh",
        zIndex: "9999",
        backgroundColor: "#f8f9fa", // Couleur de fond claire
        overflow: "auto", // Permettre le scroll
      });

      // Masquer le scroll du body
      document.body.style.overflow = "hidden";

      this.isFullscreen = true;
      this.fullscreenElement = mainArea;

      // Force un reflow pour s'assurer que les styles sont appliqués
      this.$nextTick(() => {
        mainArea.classList.add("fullscreen-mode");
      });

      console.log("Mode plein écran activé pour main-area");
    },

    /**
     * Quitte le mode plein écran personnalisé
     */
    exitCustomFullscreen() {
      if (!this.fullscreenElement) return;

      // Supprimer la classe avant de restaurer les styles
      this.fullscreenElement.classList.remove("fullscreen-mode");

      // Restaurer les styles originaux
      Object.assign(this.fullscreenElement.style, this.originalStyles);

      // Restaurer le scroll du body
      document.body.style.overflow = "";

      this.isFullscreen = false;
      this.fullscreenElement = null;
      this.originalStyles = {};

      console.log("Mode plein écran désactivé");
    },
    /**
     * Gère les touches clavier (notamment Echap pour quitter le plein écran)
     */
    handleKeydown(event) {
      if (event.key === "Escape" && this.isFullscreen) {
        this.exitCustomFullscreen();
      }
    },

    generateSessionReport() {
      // Calculer les métriques de la session
      const avgBpm = this.calculateAverage(this.exerciseData.map((d) => d.bpm));
      const avgCC = this.calculateAverage(this.exerciseData.map((d) => d.cc));

      // Créer le tachogramme pour la relecture
      const arrayTacho = this.exerciseData.map((data, index) => ({
        x: index * 125 + 517125, // Intervalle de 125ms comme dans l'exemple
        y: data.bpm,
      }));

      const tabCC = this.exerciseData.map((data, index) => ({
        x: index * 125 + 517125,
        y: data.cc,
      }));
      // Calculer les pourcentages de distribution
      const pct1 =
        this.exerciseData.length > 0
          ? Math.round(
              this.calculateAverage(this.exerciseData.map((d) => d.pctT1 || 0))
            )
          : 0;
      const pct2 =
        this.exerciseData.length > 0
          ? Math.round(
              this.calculateAverage(this.exerciseData.map((d) => d.pctT2 || 0))
            )
          : 0;
      const pct3 =
        this.exerciseData.length > 0
          ? Math.round(
              this.calculateAverage(this.exerciseData.map((d) => d.pctT3 || 0))
            )
          : 0;

      return {
        ArrayTacho: arrayTacho,
        TabCC: tabCC,
        AverageCC: Math.round(avgCC),
        Difficulty: 0,
        TimeCompletion: this.exerciseTime,
        Timer: 300, // 5 minutes par défaut
        BpmCC: Math.round(avgBpm),
        Pct1: pct1,
        Pct2: pct2,
        Pct3: pct3,
        Cpm: 0,
        Profil: null,
        Inspi: 4.5,
        PauseInspi: 0,
        Expi: 5.5,
        PauseExpi: 0,
        sessionStartTime: Date.now() - this.exerciseTime * 1000,
        sessionEndTime: Date.now(),
        totalDuration: this.exerciseTime,
      };
    },

    calculateAverage(values) {
      if (values.length === 0) return 0;
      return values.reduce((sum, value) => sum + value, 0) / values.length;
    },

    sendDataToReplayService(sessionData) {
      this.$root.$emit("open-replay-component", sessionData);
    },

    handleResizeMobile() {
      this.isMobile = window.innerWidth <= 480;
    },
  },
};
</script>
<style scoped>
.moniteur-expert {
  display: flex;
  flex-direction: column;
  font-family: Arial, sans-serif;
  position: relative;
}

/* Header Section */
.header-section {
  margin-bottom: 30px;
}

.title-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

h1 {
  color: #333;
  margin: 0;
}

.header-controls {
  display: flex;
  gap: 15px;
  align-items: center;
}

.control-button {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: none;
  border-radius: 8px;
  background-color: #f8f9fa;
  color: #495057;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.control-button:hover {
  background-color: #e9ecef;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

/* Statut de connexion */
.connection-status {
  display: flex;
  align-items: center;
  font-size: 16px;
  color: #666;
}

.status-indicator {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-right: 8px;
}

.status-indicator.connected {
  background-color: #4caf50;
  box-shadow: 0 0 5px #4caf50;
}

.status-indicator.waiting {
  background-color: #ffc107;
  box-shadow: 0 0 5px #ffc107;
}

.status-indicator.disconnected {
  background-color: #f44336;
  box-shadow: 0 0 5px #f44336;
}

.exercise-status {
  margin-left: 15px;
  color: #489181;
  font-weight: 600;
}

.card {
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  /* padding-right: 10px; */
}

.tachogram-container {
  position: relative;
  min-height: 350px;
  /* border: 2px dashed #489181; */
}

.timer-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10;
  display: flex;
  justify-content: center;
  pointer-events: none;
  /* pas de height ni de top en pourcentage */
}

.timer-display-centered {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  color: #489181;
  border: 2px solid #489181;
  box-shadow: 0 2px 8px rgba(72, 145, 129, 0.2);
  backdrop-filter: blur(5px);
  margin-top: 2.3em; /* ↓↓↓ Plus bas par défaut ! */
}

@media (max-width: 768px) {
  .timer-display-centered {
    margin-top: 1.7em; /* ↓↓↓ Un peu plus bas aussi sur tablette */
  }
}
@media (max-width: 480px) {
  .timer-display-centered {
    margin-top: 1.3em; /* ↓↓↓ Et un peu plus bas sur mobile */
  }
}

.tachogram-wrapper {
  position: relative;
}

/* === STYLES POUR LES WARNINGS CAPTEUR === */

/* Warning overlay sur l'écran de démarrage */
.sensor-warning-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(3px);
  z-index: 20;
  border-radius: 8px;
}

.sensor-warning-card {
  background: linear-gradient(135deg, #fff3cd, #ffeaa7);
  border: 3px solid #f39c12;
  border-radius: 16px;
  padding: 10px;
  text-align: center;
  box-shadow: 0 8px 32px rgba(243, 156, 18, 0.3);
  max-width: 400px;
  animation: warningPulse 2s infinite;
}

.warning-icon-large {
  font-size: 48px;
  margin-bottom: 20px;
  animation: bounce 1s infinite;
}

.sensor-warning-card h3 {
  color: #d68910;
  margin: 0 0 15px 0;
  font-size: 24px;
  font-weight: 700;
}

.sensor-warning-card p {
  color: #856404;
  margin: 0 0 20px 0;
  font-size: 16px;
  line-height: 1.4;
}

.warning-details {
  padding: 12px;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 8px;
  border: 1px solid #f39c12;
}

.bpm-indicator {
  font-weight: 600;
  color: #d68910;
  font-size: 14px;
}

/* Contenu flou quand warning actif */
.start-content-center.blurred {
  filter: blur(2px);
  opacity: 0.3;
  pointer-events: none;
}

/* Bouton désactivé */
.start-exercise-btn:disabled {
  background: #6c757d;
  cursor: not-allowed;
  opacity: 0.6;
}

.start-exercise-btn:disabled:hover {
  background: #6c757d;
  transform: none;
  box-shadow: 0 4px 16px rgba(108, 117, 125, 0.3);
}

/* Warning pendant l'exercice */
.sensor-warning-during-exercise {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 15;
  margin-bottom: 15px;
}

.warning-banner {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 15px 20px;
  background: linear-gradient(135deg, #fff3cd, #ffeaa7);
  border: 2px solid #f39c12;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(243, 156, 18, 0.2);
  animation: warningSlideIn 0.3s ease-out;
}

.warning-banner .warning-icon {
  font-size: 24px;
  animation: bounce 1s infinite;
}

.warning-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.warning-content strong {
  color: #d68910;
  font-size: 16px;
  font-weight: 700;
}

.warning-subtitle {
  color: #856404;
  font-size: 14px;
  font-weight: 500;
}

.bpm-value {
  color: #d68910;
  font-weight: 700;
  font-size: 18px;
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 20px;
  border: 2px solid #f39c12;
  min-width: 80px;
  text-align: center;
}

/* Animations */
@keyframes warningPulse {
  0%,
  100% {
    transform: scale(1);
    box-shadow: 0 8px 32px rgba(243, 156, 18, 0.3);
  }
  50% {
    transform: scale(1.02);
    box-shadow: 0 12px 48px rgba(243, 156, 18, 0.4);
  }
}

@keyframes bounce {
  0%,
  20%,
  50%,
  80%,
  100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-8px);
  }
  60% {
    transform: translateY(-4px);
  }
}

@keyframes warningSlideIn {
  from {
    transform: translateY(-100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

/* === FIN STYLES WARNINGS === */

/* Bouton de démarrage dans le tachogramme */
.start-button-container {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  border-radius: 8px;
  /* border: 2px dashed #489181; */
  position: relative;
}

.start-content-center {
  text-align: center;
  padding: 40px;
  transition: all 0.3s ease;
}

.start-icon {
  margin-bottom: 30px;
  display: flex;
  justify-content: center;
  animation: pulse 2s infinite;
}

.start-content-center h2 {
  color: #333;
  margin-bottom: 15px;
  font-size: 28px;
  font-weight: 600;
}

.start-content-center p {
  color: #666;
  margin-bottom: 30px;
  font-size: 16px;
  line-height: 1.5;
}

.start-exercise-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 16px 32px;
  background: linear-gradient(135deg, #489181, #3a7268);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 16px rgba(72, 145, 129, 0.3);
  margin: 0 auto;
}

.start-exercise-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(72, 145, 129, 0.4);
  background: linear-gradient(135deg, #3a7268, #2d5850);
}

@keyframes pulse {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
  100% {
    opacity: 1;
  }
}

/* Grille des métriques */
.metrics-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
}

.metric-card {
  background-color: #fff;
  border-radius: 0px 0px 0px 8px;
  /* box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1); */
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  position: relative;
  border-top: 2px solid #f0f0f0;
}

.metric-title {
  color: #555;
  font-size: 14px;
  font-weight: 600;
  margin: 15px 0 0 0;
  text-align: center;
  width: 100%;
  padding-top: 10px;
  border-top: 2px solid #f0f0f0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.visibility-toggle {
  position: absolute;
  top: 8px;
  right: 8px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  color: #adb5bd;
  transition: all 0.2s ease;
  z-index: 2;
}

.visibility-toggle:hover {
  background-color: #f1f3f5;
  color: #495057;
}

.metric-card-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 2px dashed #e9ecef;
  border-radius: 8px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  background-color: #f8f9fa;
  min-height: 200px; /* Assurer une hauteur cohérente */
  box-sizing: border-box;
}

.metric-card-placeholder:hover {
  background-color: #e9ecef;
  border-color: #dee2e6;
}

.placeholder-icon {
  color: #adb5bd;
  margin-bottom: 15px;
}

.placeholder-icon svg {
  width: 32px;
  height: 32px;
}

.placeholder-text {
  color: #495057;
  font-weight: 500;
  font-size: 14px;
  text-align: center;
}

/* Container pour les métriques avec position relative */
.metrics-container {
  position: relative;
}

/* Overlay pour le bouton Terminer */
.stop-button-overlay {
  position: absolute;
  top: -15px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
  pointer-events: none;
}

.stop-btn-overlay {
  background-color: #dc3545;
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(220, 53, 69, 0.3);
  transition: all 0.3s ease;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  pointer-events: auto;
  white-space: nowrap;
}

.stop-btn-overlay:hover {
  background-color: #c82333;
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(220, 53, 69, 0.4);
}

.test-replay-btn {
  padding: 10px 22px;
  font-size: 1.08em;
  font-weight: 600;
  color: #fff;
  background: #1b715c;
  border: none;
  border-radius: 9px;
  cursor: pointer;
  margin-bottom: 10px;
  transition: background 0.2s;
}
.test-replay-btn:hover {
  background: #185a48;
}

/* Animation fluide pour les transitions */
.main-area {
  transition: all 0.3s ease-in-out;
}

/* === STYLES POUR LE MODE PLEIN ÉCRAN === */

/* Styles pour le mode plein écran - Gestion des espaces */
.main-area.fullscreen-mode {
  display: flex;
  flex-direction: column;
  overflow: auto;
  padding: 15px;
  box-sizing: border-box;
}

.main-area.fullscreen-mode .tachogram-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
  min-height: 60vh; /* Assure une hauteur minimum pour le tachogramme */
}

.main-area.fullscreen-mode .tachogram-wrapper {
  flex: 1;
  height: 100%;
}

.main-area.fullscreen-mode .metrics-container {
  flex-shrink: 0;
  margin-top: auto;
  min-height: 35vh; /* Hauteur minimum pour les métriques */
}

.main-area.fullscreen-mode .metrics-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 15px;
  padding: 20px;
  height: 100%;
}

.main-area.fullscreen-mode .metric-card {
  min-height: 150px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border-radius: 12px;
}

.main-area.fullscreen-mode .metric-title {
  font-size: 14px;
  margin-top: 10px;
  padding-top: 10px;
}

/* Masquer le bouton terminer en mode plein écran */
.main-area.fullscreen-mode .stop-button-overlay {
  display: none;
}

/* Indicateur de mode plein écran amélioré */
.main-area.fullscreen-mode::before {
  content: "Mode plein écran - Échap pour quitter";
  position: fixed;
  top: 20px;
  right: 20px;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  z-index: 10001;
  opacity: 1;
  animation: fadeInOut 5s ease-in-out;
}

@keyframes fadeInOut {
  0%,
  100% {
    opacity: 0;
  }
  15%,
  85% {
    opacity: 1;
  }
}

/* Responsive pour le mode plein écran */
@media (max-width: 1200px) {
  .main-area.fullscreen-mode {
    padding: 12px;
  }

  .main-area.fullscreen-mode .tachogram-container {
    min-height: 55vh;
    margin-bottom: 15px;
  }

  .main-area.fullscreen-mode .metrics-container {
    min-height: 40vh;
  }

  .main-area.fullscreen-mode .metrics-grid {
    gap: 12px;
    padding: 15px;
  }

  .main-area.fullscreen-mode .metric-card {
    min-height: 130px;
    padding: 12px;
  }
}

@media (max-width: 768px) {
  .main-area.fullscreen-mode {
    padding: 10px;
  }

  .main-area.fullscreen-mode .tachogram-container {
    min-height: 50vh;
    margin-bottom: 12px;
  }

  .main-area.fullscreen-mode .metrics-container {
    min-height: 45vh;
  }

  .main-area.fullscreen-mode .metrics-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
    padding: 12px;
  }

  .main-area.fullscreen-mode .metric-card {
    min-height: 120px;
    padding: 10px;
  }

  .main-area.fullscreen-mode .metric-title {
    font-size: 12px;
    margin-top: 8px;
    padding-top: 8px;
  }
}

@media (max-width: 480px) {
  .main-area.fullscreen-mode {
    padding: 8px;
  }

  .main-area.fullscreen-mode .tachogram-container {
    min-height: 45vh;
    margin-bottom: 10px;
  }

  .main-area.fullscreen-mode .metrics-container {
    min-height: 50vh;
  }

  .main-area.fullscreen-mode .metrics-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
    padding: 8px;
  }

  .main-area.fullscreen-mode .metric-card {
    min-height: 100px;
    padding: 8px;
  }

  .main-area.fullscreen-mode .metric-title {
    font-size: 10px;
    margin-top: 6px;
    padding-top: 6px;
  }
}

/* Mode portrait mobile */
@media (max-width: 480px) and (orientation: portrait) {
  .main-area.fullscreen-mode .tachogram-container {
    min-height: 40vh;
  }

  .main-area.fullscreen-mode .metrics-container {
    min-height: 55vh;
  }

  .main-area.fullscreen-mode .metrics-grid {
    grid-template-columns: 1fr;
    gap: 6px;
  }

  .main-area.fullscreen-mode .metric-card {
    min-height: 80px;
    padding: 6px;
  }
}

/* Mode paysage mobile */
@media (max-width: 768px) and (orientation: landscape) {
  .main-area.fullscreen-mode {
    flex-direction: row;
    padding: 8px;
  }

  .main-area.fullscreen-mode .tachogram-container {
    flex: 2;
    margin-bottom: 0;
    margin-right: 10px;
    min-height: auto;
    height: 100%;
  }

  .main-area.fullscreen-mode .metrics-container {
    flex: 1;
    margin-top: 0;
    min-height: auto;
    height: 100%;
  }

  .main-area.fullscreen-mode .metrics-grid {
    grid-template-columns: 1fr;
    gap: 6px;
    padding: 8px;
    height: 100%;
  }

  .main-area.fullscreen-mode .metric-card {
    min-height: 70px;
    padding: 6px;
  }

  .main-area.fullscreen-mode .metric-title {
    font-size: 9px;
    margin-top: 4px;
    padding-top: 4px;
  }
}

/* === FIN STYLES MODE PLEIN ÉCRAN === */

/* Responsive design normal */
@media (max-width: 768px) {
  .title-container {
    flex-direction: column;
    gap: 15px;
    align-items: flex-start;
  }

  .header-controls {
    align-self: stretch;
    justify-content: space-between;
    flex-wrap: wrap;
  }

  .timer-display-centered {
    font-size: 12px;
    padding: 4px 10px;
    gap: 4px;
  }

  .timer-display-centered svg {
    width: 14px;
    height: 14px;
  }

  .metrics-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .metric-title {
    font-size: 12px;
    margin-top: 10px;
    padding-top: 8px;
  }

  .start-content-center {
    padding: 30px 20px;
  }

  .start-content-center h2 {
    font-size: 24px;
  }

  .start-exercise-btn {
    padding: 14px 28px;
    font-size: 16px;
  }

  /* Responsive pour les warnings */
  .sensor-warning-card {
    margin: 20px;
    padding: 30px 20px;
  }

  .sensor-warning-card h3 {
    font-size: 20px;
  }

  .warning-banner {
    padding: 12px 15px;
    gap: 10px;
  }

  .warning-banner .warning-icon {
    font-size: 20px;
  }

  .warning-content strong {
    font-size: 14px;
  }

  .warning-subtitle {
    font-size: 12px;
  }

  .bpm-value {
    font-size: 16px;
    padding: 6px 12px;
    min-width: 70px;
  }

  /* Responsive pour le bouton terminer */
  .stop-button-overlay {
    top: -12px;
  }

  .stop-btn-overlay {
    padding: 6px 12px;
    font-size: 13px;
    gap: 6px;
  }
}

@media (max-width: 480px) {
  .metrics-grid {
    grid-template-columns: 1fr;
  }

  .metric-title {
    font-size: 11px;
    margin-top: 8px;
    padding-top: 6px;
  }

  .control-button {
    padding: 8px 12px;
    font-size: 13px;
  }

  .timer-display-centered {
    font-size: 11px;
    padding: 3px 8px;
    gap: 3px;
  }

  .timer-display-centered svg {
    width: 12px;
    height: 12px;
  }

  .start-exercise-btn {
    padding: 12px 24px;
    font-size: 15px;
  }

  .start-button-container {
    min-height: 250px;
  }

  /* Responsive mobile pour les warnings */
  .sensor-warning-card {
    margin: 10px;
    padding: 25px 15px;
  }

  .warning-icon-large {
    font-size: 36px;
    margin-bottom: 15px;
  }

  .sensor-warning-card h3 {
    font-size: 18px;
    margin-bottom: 10px;
  }

  .sensor-warning-card p {
    font-size: 14px;
    margin-bottom: 15px;
  }

  .warning-banner {
    padding: 10px 12px;
    gap: 8px;
    flex-direction: column;
    text-align: center;
  }

  .bmp-value {
    margin-top: 8px;
  }

  /* Responsive pour le bouton terminer */
  .stop-button-overlay {
    top: -10px;
  }

  .stop-btn-overlay {
    padding: 5px 10px;
    font-size: 12px;
    gap: 4px;
    border-radius: 16px;
  }

  .stop-btn-overlay svg {
    width: 16px;
    height: 16px;
  }
}

/* Responsive design mobile (max densité pour petit écran) */
@media (max-width: 480px) {
  .moniteur-expert {
    padding: 6px; /* <--- plus petit */
    min-height: unset;
  }

  .header-section {
    margin-bottom: 14px; /* <--- réduit */
  }
  .title-container {
    gap: 7px;
    margin-bottom: 7px;
  }
  h1 {
    font-size: 1.15em;
  }
  .test-replay-btn {
    font-size: 0.93em;
    padding: 7px 12px;
    margin-bottom: 7px;
    border-radius: 6px;
  }
  .main-area {
    margin-bottom: 10px;
  }
  .card {
    border-radius: 6px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  }
  .tachogram-container {
    min-height: 180px; /* <--- réduit */
    border-width: 1px;
  }
  .timer-display-centered {
    font-size: 10px;
    padding: 2px 5px;
    border-radius: 13px;
    gap: 2px;
    border-width: 1px;
  }
  .timer-display-centered svg {
    width: 10px;
    height: 10px;
  }
  .start-button-container {
    min-height: 100px; /* <--- réduit */
  }
  .start-content-center {
    padding: 12px 4px;
  }
  .start-icon {
    margin-bottom: 13px;
  }
  .start-content-center h2 {
    font-size: 17px;
    margin-bottom: 6px;
  }
  .start-content-center p {
    font-size: 13px;
    margin-bottom: 8px;
  }
  .start-exercise-btn {
    padding: 7px 11px;
    font-size: 14px;
    border-radius: 7px;
  }
  /* Sensor warnings mobiles */
  .sensor-warning-card {
    margin: 3px;
    padding: 10px 3px;
    border-radius: 10px;
  }
  .warning-icon-large {
    font-size: 22px;
    margin-bottom: 7px;
  }
  .sensor-warning-card h3 {
    font-size: 14px;
    margin-bottom: 6px;
  }
  .sensor-warning-card p {
    font-size: 12px;
    margin-bottom: 7px;
  }
  .metrics-grid {
    grid-template-columns: 1fr;
    gap: 8px; /* <--- Ajout */
    margin-bottom: 13px;
  }
  .metric-card {
    border-radius: 5px;
    padding: 5px 0 4px 0; /* <--- Ajout */
    min-height: 85px; /* <--- Ajout */
  }
  .metric-title {
    font-size: 10px;
    margin-top: 6px;
    padding-top: 4px;
    border-top-width: 1px;
  }
  .bpm-value {
    font-size: 12px;
    padding: 3px 7px;
    min-width: 30px;
  }
  .warning-banner {
    padding: 6px 6px;
    gap: 4px;
    flex-direction: column;
  }
}

@media (max-width: 350px) {
  /* Ultra tiny phone ! */
  .moniteur-expert {
    padding: 1px;
  }
  .start-content-center {
    padding: 3px 0;
  }
}
</style>
